// Join the Plaza at a glance (wave 13, docs/build/WAVE13-PLACE.md pillar 5): the global bar's Plaza entry and Home's
// Plaza card, both with live presence ("23 here now") and both launching straight into the world in the Banger app.
//
// Presence is rooms' public directory: `GET /v1/rooms/<world>` → `{instances: [{instance, n, cap}]}`, summed. Read once
// per 30 s and shared by every caller; no count (rooms down, or nobody in) hides the number, never the way in.
// The world is events.ts's WORLD, so presence, the calendar and Join always name the same rooms world.
// Phones can't run the app: their Plaza entry leads to what's on (/events), not to a desktop download.

import { api } from "./api.ts";
import { h, link } from "./dom.ts";
import { joinLink, PLAZA_GAME, WORLD } from "./events.ts";
import { detectPlatform, downloadPath, launch } from "./play.ts";
import { navigate } from "./router.ts";
import { gatePlatform } from "./zones/download/gate.ts";
import { PLAZA_BLURB, PLAZA_WORLD } from "./zones/events/programme.ts";

/** How many people are in the world now: the sum over its instances; null when rooms didn't answer usefully. */
export function presenceFrom(raw: unknown): number | null {
  const list = raw && typeof raw === "object" ? (raw as { instances?: unknown }).instances : null;
  if (!Array.isArray(list)) return null;
  let n = 0;
  for (const i of list) {
    const k = i && typeof i === "object" ? (i as { n?: unknown }).n : null;
    if (typeof k === "number" && Number.isFinite(k) && k > 0) n += Math.floor(k);
  }
  return n;
}

/** "23 here now", "1 here now"; null for nobody or unknown (the count is hidden then). */
export function presenceText(n: number | null): string | null {
  return n && n > 0 ? `${n.toLocaleString("en")} here now` : null;
}

let cache: { at: number; p: Promise<number | null> } | null = null;
/** Presence in the Plaza's world, at most one request per 30 s. */
export function loadPresence(world = WORLD): Promise<number | null> {
  if (!cache || Date.now() - cache.at > 30_000)
    cache = { at: Date.now(), p: api("rooms", `/rooms/${encodeURIComponent(world)}`, { auth: false }).then(presenceFrom, () => null) };
  return cache.p;
}

/** A phone or tablet: no app to launch, so the Plaza entry shows what's on instead. */
export function onPhone(): boolean {
  return gatePlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0, null, detectPlatform) === "mobile";
}

/** Opens the Plaza in Banger (an event's instance when given); without the app, the download page. */
export function joinPlaza(instance = ""): void {
  launch(PLAZA_GAME, { link: joinLink(instance), onMissing: () => navigate(downloadPath(PLAZA_GAME)) });
}

/** Fills `el` with the presence line once rooms answers; leaves it hidden when there's no count. */
function presence(el: HTMLElement, text: (n: number) => string = (n) => presenceText(n)!): HTMLElement {
  el.hidden = true;
  void loadPresence().then((n) => {
    if (!n) return;
    el.textContent = text(n);
    el.hidden = false;
  });
  return el;
}

/** The global bar's Plaza entry: "Plaza" with a live dot and the count; a click opens the world. */
export function plazaNavLink(): HTMLElement {
  const phone = onPhone();
  const count = presence(h("span", { class: "plaza-count", "aria-hidden": "true" }), (n) => {
    a.setAttribute("aria-label", `Plaza, ${presenceText(n)}`);
    return n.toLocaleString("en");
  });
  const a = h(
    "a",
    {
      class: "global-link plaza-link",
      href: phone ? "/events#venues" : joinLink(""),
      title: phone ? "What's on in the Plaza" : "Join the Plaza (opens the Banger app)",
      "aria-label": "Plaza",
      onclick: (e: Event) => {
        e.preventDefault();
        if (phone) navigate("/events#venues");
        else joinPlaza();
      },
    },
    h("span", { class: "plaza-dot", "aria-hidden": "true" }),
    h("span", { class: "plaza-label" }, "Plaza"),
    count,
  );
  return a;
}

/** Home's Plaza card: the world rendered in its own light, who's there now, and Join. */
export function plazaCard(): HTMLElement {
  const phone = onPhone();
  const here = presence(h("span", { class: "plaza-here" }));
  return h(
    "section",
    { class: "plaza-card", "aria-label": "The Plaza" },
    h(
      "div",
      { class: "plaza-card-text" },
      h("p", { class: "eyebrow" }, h("span", { class: "plaza-dot", "aria-hidden": "true" }), "The Plaza · open now", here),
      h("h2", { class: "plaza-card-title" }, "Join the Plaza"),
      h("p", { class: "plaza-card-blurb" }, PLAZA_BLURB[PLAZA_WORLD]),
      h(
        "div",
        { class: "plaza-card-actions" },
        phone
          ? link("/events", { class: "btn-play small" }, "What's on")
          : h(
              "a",
              {
                class: "btn-play small",
                href: joinLink(""),
                "aria-label": "Join the Plaza (opens the Banger app)",
                onclick: (e: Event) => {
                  e.preventDefault();
                  joinPlaza();
                },
              },
              h("span", { "aria-hidden": "true", class: "tri" }, "▶"),
              "Join now",
            ),
        phone ? null : link("/events", { class: "btn ghost small" }, "What's on"),
      ),
      phone ? h("p", { class: "plaza-card-note" }, "Join from the Banger app on your computer.") : null,
    ),
  );
}
